(ns frontend.view.modals.curtains.filter-curtain.view
  (:require
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.fields.filter-types :as filter-types]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.curtains.filter-curtain.events :as events]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.radio-group.view :as radio-group]
   [ui.components.select.select :as select]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]))

(defonce modal-key :curtain/filter)

(defn filter-editor
  [{:keys [field-schema editor-type value-key pipeline filter-state]}]
  (let [is-select?   (some #{editor-type} [:editor.type/select :editor.type/multi-select])
        entity-types (if (some #{:entity.type/asset :entity.type/customer}
                               (:field/entity-types field-schema))
                       (:pipeline/target-asset-types pipeline)
                       (:field/entity-types field-schema))
        options      (when is-select?
                       (cond
                         (seq (:options field-schema))
                         (->> (:options field-schema)
                              (mapv (fn [{:keys [key value label]}]
                                      {:value (or value key)
                                       :label label})))
                         (:named-value-attribute field-schema)
                         (->> (fu/named-value-options field-schema (:db/id pipeline))
                              (mapv (fn [e]
                                      {:value (:db/id e)
                                       :label (entity/display-string e)})))
                         (seq entity-types)
                         (->> (fu/option-entities pipeline
                                                  (assoc field-schema
                                                         :field/entity-types entity-types))
                              (mapv (fn [e]
                                      {:value (:db/id e)
                                       :label (entity/display-string e)})))))]
    [data-input/view
     (cond-> {:editor-type editor-type
              :data-type   (:field/data-type field-schema)
              :value       (some-> filter-state deref value-key)
              :full-width? true
              :on-change   (fn [new-value & _]
                             (events/set-bound-value filter-state value-key new-value))}
       is-select?
       (assoc :options options
              :class "flex-wrap h-auto")
       (seq entity-types)
       (assoc :field/entity-types entity-types))]))

(defn value-input
  [{:keys [filter-state operand pipeline field-schema]}]
  (let [filter-type (->> field-schema
                         :field/default-visibility
                         :filter)
        filter-value-editor (filter-types/editor-by-filter-type filter-type operand)
        operand-editor (filter-types/operand-editor filter-value-editor operand)]
    (when (and filter-value-editor
               (not (some #{filter-value-editor} [:editor.type/none])))
      [:div
       [:div
        {:class "grid grid-cols-2 gap-3 relative"}
        [filter-editor
         {:editor-type  operand-editor
          :value-key    :lower-bound-value
          :pipeline     pipeline
          :filter-state filter-state
          :field-schema field-schema}]
        (when (some #{operand} events/multi-value-operands)
          [filter-editor
           {:editor-type  operand-editor
            :value-key    :upper-bound-value
            :pipeline     pipeline
            :filter-state filter-state
            :field-schema field-schema}])]])))

(defn single-filter-view
  [{:keys [filter-state
           key-state
           on-key-change!
           pipeline
           field-options]}]
  (let [field-key @key-state
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema field-key)
        field-display-name (or (->> field-schema
                                    :field/translations
                                    :en)
                               (loc/field-name field-key field-schema)
                               (when field-key
                                 (do (logging/error (str "No translation for field " field-key))
                                     (attribute/attribute-label tenant-schema field-key))))
        filter-type (->> field-schema
                         :field/default-visibility
                         :filter)
        filter-operands (get filter-types/operands-by-filter-type filter-type)]
    ;; column to be filtered will be chosen from a list of field-options:
    [:div
     {:class "flex flex-col gap-4"}
     (when (and (not field-key)
                (seq field-options))
       [:div.zap-select.zap-form__light-grey__green__sand.form-group
        [:label.zap-label.control-label (loc/tr :filter-curtain-show-only)]
        [select/view
         {:value              field-key
          :menu-portal-target (dg/element-by-id "react-select-menu-container")
          :options            field-options
          :on-change          (fn [v]
                                (events/set-field-key key-state v)
                                (when on-key-change!
                                  (on-key-change! v)))}]])

     (when (= filter-type :filter.type/boolean)
       [:div
        (loc/tr :filter-curtain-switch-info)])
     (->> filter-operands
          (map (fn [oper-key]
                 ^{:key (str "filter-curtain-operand-" oper-key)}
                 [:div
                  {:class "flex flex-col gap-2"}
                  [radio-group/radio-elem
                   {:checked?  (= oper-key (:operand @filter-state))
                    :value     oper-key
                    :label     (loc/tr oper-key [field-display-name])
                    :on-change (fn [_]
                                 (swap! filter-state assoc :operand oper-key))}]
                  (when (and (= oper-key (:operand @filter-state))
                             (not (events/bool-value-operand? oper-key)))
                    [:div
                     {:class "ps-6"}
                     [value-input
                      {:filter-state filter-state
                       :operand      (:operand @filter-state)
                       :pipeline     pipeline
                       :data         @filter-state
                       :field-schema field-schema}]])]))
          (doall))]))

(defn or-filter-view
  [{:keys [filter-state key-state get-label] :as args}]
  (let [child-filter-count (or (some->> @filter-state
                                        :filter/child-filters
                                        (count))
                               0)]
    [:div
     {:class "flex flex-col gap-4"}
     (if (= 0 child-filter-count)
       [single-filter-view args]
       (->> @filter-state
            :filter/child-filters
            (map-indexed
             (fn [idx [filter-key _]]
               ^{:key (str "filter-" idx)}
               [:div
                {:class "flex flex-col gap-4"}
                (when-not (some #{idx} [0 child-filter-count])                                      ;; don't add to first choice
                  [:div
                   {:class "uppercase text-center font-bold text-base-light-text-paler"}
                   (loc/tr :or)])
                [:div
                 {:class "border border-solid border-base-light-border-paler rounded"}
                 [:h4
                  {:class "p-2 flex items-center justify-between
                           border-b border-solid border-base-light-border-paler"}
                  [:span
                   (get-label filter-key)]
                  [button/square-secondary-open
                   {:tooltip    (loc/tr :filter-curtain/remove-filter-group)
                    :aria-label (loc/tr :filter-curtain/remove-filter-group)
                    :on-click   (fn [_]
                                  (let [remove-idx idx]
                                    (swap! filter-state assoc
                                           :filter/child-filters
                                           (->> @filter-state
                                                :filter/child-filters
                                                (keep-indexed (fn [idx item]
                                                                (when (not= idx remove-idx)
                                                                  item)))
                                                (vec)))))}
                   [:i.fal.fa-trash-alt]]]
                 [:div
                  {:class "p-3"}
                  (let [child-ks (r/cursor filter-state [:filter/child-filters idx 0])
                        child-fs (r/cursor filter-state [:filter/child-filters idx 1])]
                    [single-filter-view
                     (assoc args
                            :key-state child-ks
                            :on-key-change! (fn [_v]
                                              (reset! key-state (events/resolve-or-key
                                                                 filter-state))
                                              (deref key-state))
                            :filter-state child-fs)])]]]))
            (doall)))
     [:div
      {:class "my-2"}
      [button/sm-primary-solid
       {:on-click (fn [_]
                    (let [old-key @key-state
                          old-filter @filter-state
                          [_ new-filter] (events/merge-filters [old-key old-filter]
                                                               [nil {}])]
                      (reset! filter-state new-filter)
                      (reset! key-state (events/resolve-or-key filter-state))
                      (deref filter-state)
                      (deref key-state)))}
       (loc/tr :add-or-filter)]]]))

(defn view
  [{:keys [state props _shown? _topmost? _close-modal!]}]
  (let [{:keys [field-key data]} props
        filter-state (r/cursor state [:data])
        key-state (r/cursor state [:filter-key])
        error-state (r/cursor state [:error-text])
        options (:options props)
        field-label-fn (:field-label-fn props)
        tenant-schema @session/tenant-schema
        get-label (fn [k]
                    (or (when (fn? field-label-fn)
                          (field-label-fn k))
                        (attribute/attribute-label tenant-schema k)))
        field-options (->> options
                           (map (fn [[k _]]
                                  {:value k
                                   :label (get-label k)}))
                           (sort-by :label))]
    (reset! key-state field-key)
    (reset! filter-state (or data {}))
    (fn [{:keys [props shown? topmost? close-modal!]}]
      (let [{:keys [field-key options pipeline on-ok!]} props]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         (if (and (not field-key)
                  (seq options))
           (loc/tr :add-new-filter)
           (loc/tr :filter-curtain-header-text
                   [(if-let [field-key @key-state]
                      (if (and (keyword? field-key)
                               (= "or" (namespace @key-state)))
                        (loc/tr :or-filter)
                        (get-label field-key)))]))
         [:<>
          (when @error-state
            [:div.zap-modal__body__error
             (str @error-state)])

          [or-filter-view
           {:filter-state  filter-state
            :key-state     key-state
            :error-state   error-state
            :pipeline      pipeline
            :field-options field-options
            :get-label     get-label}]]
         [:<>
          [modal/left-cancel-only close-modal! nil]
          (let [valid? (events/valid-state? key-state filter-state)]
            [modal/right-single-button
             nil
             (not valid?)
             (fn [_]
               ;;remove unnecessary lower/upper-bound-values from state:
               (events/cleanup-local-state! filter-state)
               (when valid?
                 (when (and (not= field-key @key-state)
                            field-key)
                   (on-ok! field-key {:filter/type :filter.type/none}))
                 (on-ok! @key-state @filter-state))
               (close-modal!))
             (loc/tr :apply-filter)
             nil])]]))))
